'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale, useTranslations } from 'next-intl';
import { OptimizedImage } from '@/components/images/optimized-image';
import { ThumbWithOrigin } from '@/components/market/identity/identity-marks';
import type { MarketIdentityData } from '@/components/market/identity/market-identity';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { Icon } from '@/components/ui/icon';
import { CircleArrowDown02IconSolid } from '@/components/ui/icon/glyphs';
import { TickerChip } from '@/components/ui/ticker-display';
import { D9_SCALE } from '@/config/solana';
import { TOAST_IDS } from '@/config/toast-ids';
import { useFormattedCurrency } from '@/lib/currency';
import { formatPercentValue } from '@/lib/format/format-number';
import { formatRelativeTime } from '@/lib/format-relative-time';
import { showToast } from '@/lib/ui/toast';
import { cn } from '@/lib/ui/utils';
import { winnerHueFromMint } from '@/lib/winners/winner-hue';
import { useChartSettingsStore } from '@/stores/chart-settings';
export interface WinnerTokenHeaderProps {
  mint: string;
  /** Winning outcome name (the headline). */
  name: string;
  /** Parent market title — the first meta-row entry. */
  marketTitle: string;
  ticker: string | null;
  imageUrl: string | null;
  imageNsfw: boolean;
  /** ISO timestamp the token graduated — drives the "2d ago" meta entry. */
  graduatedAtIso: string | null;
  /** Realtime market cap (D6). The parent subscribes to the winner-row cache
   *  via `useLiveListData`, so this prop re-feeds live on every NATS AMM patch
   *  (`useGetWinnersField` can't be used off a list `<Items>`). */
  marketCap: string | null;
  /** Realtime spot price (D9), re-fed live alongside `marketCap`. Shown in the
   *  header stat when the chart's Price/Market Cap toggle is on Price. */
  price: string | null;
  change24hBps: string | null;
  canonicalPath: string;
  /** Influencer / Featured origin mark on the outcome image's corner. */
  marketType?: MarketIdentityData['marketType'];
}

/** Signed-bps → "▲ 32.2%" pill, tinted by sign; neutral on a 0 move. */
function ChangePill(t0) {
  const $ = _c(16);
  const {
    bps,
    locale
  } = t0;
  if (bps === null || bps === "") {
    return null;
  }
  const pct = Number(bps) / 100;
  if (!Number.isFinite(pct)) {
    return null;
  }
  let isZero;
  let t1;
  if ($[0] !== locale || $[1] !== pct) {
    const abs = Math.abs(pct);
    isZero = Math.round(abs * 10) === 0;
    t1 = formatPercentValue(abs, locale, isZero ? 1 : 0);
    $[0] = locale;
    $[1] = pct;
    $[2] = isZero;
    $[3] = t1;
  } else {
    isZero = $[2];
    t1 = $[3];
  }
  const label = `${t1}%`;
  if (isZero) {
    let t2;
    if ($[4] !== label) {
      t2 = <span className="text-caption tabular-nums text-foreground-tertiary">{label}</span>;
      $[4] = label;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  const isUp = pct > 0;
  const t2 = isUp ? "text-feedback-success-content" : "text-feedback-error-content";
  let t3;
  if ($[6] !== t2) {
    t3 = cn("inline-flex items-center gap-1 text-caption tabular-nums", t2);
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = isUp && "rotate-180";
  let t5;
  if ($[8] !== t4) {
    t5 = cn("size-3 shrink-0", t4);
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = <Icon icon={CircleArrowDown02IconSolid} className={t5} />;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== label || $[13] !== t3 || $[14] !== t6) {
    t7 = <span className={t3}>{t6}{label}</span>;
    $[12] = label;
    $[13] = t3;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}
export function WinnerTokenHeader(props) {
  const $ = _c(82);
  const locale = useLocale();
  const t = useTranslations("market.winnerDetail");
  const tHeader = useTranslations("market.header");
  const tTimeIntl = useTranslations("time");
  let t0;
  if ($[0] !== tTimeIntl) {
    t0 = (key, params) => tTimeIntl(key as Parameters<typeof tTimeIntl>[0], params);
    $[0] = tTimeIntl;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const tTime = t0;
  const {
    formatD6,
    formatBalance
  } = useFormattedCurrency();
  const isPrice = useChartSettingsStore(_temp) === "price";
  let t1;
  if ($[2] !== props.mint) {
    t1 = winnerHueFromMint(props.mint);
    $[2] = props.mint;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const hue = t1;
  const t2 = props.graduatedAtIso ?? "";
  let t3;
  if ($[4] !== t2 || $[5] !== tTime) {
    t3 = formatRelativeTime(t2, tTime, {
      style: "short"
    });
    $[4] = t2;
    $[5] = tTime;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const graduatedAgo = t3;
  let t4;
  if ($[7] !== tHeader) {
    t4 = function handleCopyTicker(symbol) {
      navigator.clipboard.writeText(symbol).then(() => {
        showToast({
          id: TOAST_IDS.TICKER_COPIED,
          message: tHeader("tickerCopied", {
            ticker: symbol
          }),
          variant: "success"
        });
      });
    };
    $[7] = tHeader;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleCopyTicker = t4;
  const t5 = props.imageUrl !== null && props.imageUrl !== "" ? props.imageUrl : null;
  let t6;
  if ($[9] !== hue || $[10] !== props.imageNsfw || $[11] !== props.mint || $[12] !== t5) {
    t6 = {
      kind: "winner" as const,
      mint: props.mint,
      hue,
      imageUrl: t5,
      nsfw: props.imageNsfw
    };
    $[9] = hue;
    $[10] = props.imageNsfw;
    $[11] = props.mint;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const imageEntity = t6;
  let t7;
  if ($[14] !== isPrice || $[15] !== t) {
    t7 = isPrice ? t("statPrice") : t("statMarketCap");
    $[14] = isPrice;
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t7) {
    t8 = <span className="text-caption leading-none text-foreground-tertiary">{t7}</span>;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== formatBalance || $[20] !== formatD6 || $[21] !== isPrice || $[22] !== props.marketCap || $[23] !== props.price) {
    t9 = <span className="text-heading-lg font-medium tabular-nums text-foreground-primary">{(isPrice ? props.price : props.marketCap) === null ? <span><span className="sr-only">Unavailable</span><span aria-hidden="true">{isPrice ? formatBalance(0, "price") : formatD6("0", "compact")}</span></span> : <AnimatedNumber value={Number(isPrice ? props.price : props.marketCap)} format={isPrice ? n => formatBalance(n / D9_SCALE, "price") : n_0 => formatD6(String(Math.round(n_0)), "compact")} animateWithColour={true} duration={400} />}</span>;
    $[19] = formatBalance;
    $[20] = formatD6;
    $[21] = isPrice;
    $[22] = props.marketCap;
    $[23] = props.price;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== locale || $[26] !== props.change24hBps) {
    t10 = <ChangePill bps={props.change24hBps} locale={locale} />;
    $[25] = locale;
    $[26] = props.change24hBps;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10 || $[29] !== t8 || $[30] !== t9) {
    t11 = <>{t8}{t9}{t10}</>;
    $[28] = t10;
    $[29] = t8;
    $[30] = t9;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  const statContent = t11;
  let t12;
  if ($[32] !== imageEntity || $[33] !== props.name) {
    t12 = <OptimizedImage variant="thumbnail" entity={imageEntity} alt={props.name} size="size-16" rounded="rounded-overlay-item" />;
    $[32] = imageEntity;
    $[33] = props.name;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== props.marketType || $[36] !== t12) {
    t13 = <ThumbWithOrigin marketType={props.marketType}>{t12}</ThumbWithOrigin>;
    $[35] = props.marketType;
    $[36] = t12;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== props.name) {
    t14 = <h1 className="line-clamp-2 min-w-0 flex-1 text-heading-lg leading-tight text-foreground-primary">{props.name}</h1>;
    $[38] = props.name;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== statContent) {
    t15 = <div className="flex shrink-0 flex-col items-end gap-1">{statContent}</div>;
    $[40] = statContent;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== t13 || $[43] !== t14 || $[44] !== t15) {
    t16 = <div className="flex items-center gap-3 sm:hidden">{t13}{t14}{t15}</div>;
    $[42] = t13;
    $[43] = t14;
    $[44] = t15;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== imageEntity || $[47] !== props.name) {
    t17 = <OptimizedImage variant="thumbnail" entity={imageEntity} alt={props.name} size="size-24" rounded="rounded-card" />;
    $[46] = imageEntity;
    $[47] = props.name;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== props.marketType || $[50] !== t17) {
    t18 = <div className="hidden sm:block"><ThumbWithOrigin marketType={props.marketType}>{t17}</ThumbWithOrigin></div>;
    $[49] = props.marketType;
    $[50] = t17;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== props.name) {
    t19 = <h1 className="hidden line-clamp-2 min-w-0 text-heading-lg leading-tight text-foreground-primary sm:block">{props.name}</h1>;
    $[52] = props.name;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  const t20 = props.ticker;
  let t21;
  if ($[54] !== tHeader) {
    t21 = tHeader("copyTicker");
    $[54] = tHeader;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  let t22;
  if ($[56] !== handleCopyTicker || $[57] !== props.ticker || $[58] !== t21) {
    t22 = <TickerChip ticker={t20} onClick={handleCopyTicker} aria-label={t21} />;
    $[56] = handleCopyTicker;
    $[57] = props.ticker;
    $[58] = t21;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] !== graduatedAgo || $[61] !== props.ticker) {
    t23 = props.ticker !== null && props.ticker !== "" && graduatedAgo !== "" ? <span aria-hidden="true">·</span> : null;
    $[60] = graduatedAgo;
    $[61] = props.ticker;
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  let t24;
  if ($[63] !== graduatedAgo) {
    t24 = graduatedAgo !== "" ? <span className="shrink-0">{graduatedAgo}</span> : null;
    $[63] = graduatedAgo;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  let t25;
  if ($[65] !== t22 || $[66] !== t23 || $[67] !== t24) {
    t25 = <div data-winner-metadata={true} className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-body text-foreground-tertiary">{t22}{t23}{t24}</div>;
    $[65] = t22;
    $[66] = t23;
    $[67] = t24;
    $[68] = t25;
  } else {
    t25 = $[68];
  }
  let t26;
  if ($[69] !== props.marketTitle) {
    t26 = props.marketTitle !== "" ? <p className="min-w-0 text-body leading-snug text-foreground-tertiary line-clamp-2">{props.marketTitle}</p> : null;
    $[69] = props.marketTitle;
    $[70] = t26;
  } else {
    t26 = $[70];
  }
  let t27;
  if ($[71] !== t19 || $[72] !== t25 || $[73] !== t26) {
    t27 = <div className="flex min-w-0 flex-1 flex-col gap-1">{t19}{t25}{t26}</div>;
    $[71] = t19;
    $[72] = t25;
    $[73] = t26;
    $[74] = t27;
  } else {
    t27 = $[74];
  }
  let t28;
  if ($[75] !== statContent) {
    t28 = <div className="hidden shrink-0 flex-col items-end gap-1 sm:flex">{statContent}</div>;
    $[75] = statContent;
    $[76] = t28;
  } else {
    t28 = $[76];
  }
  let t29;
  if ($[77] !== t16 || $[78] !== t18 || $[79] !== t27 || $[80] !== t28) {
    t29 = <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-3">{t16}{t18}{t27}{t28}</div>;
    $[77] = t16;
    $[78] = t18;
    $[79] = t27;
    $[80] = t28;
    $[81] = t29;
  } else {
    t29 = $[81];
  }
  return t29;
}
function _temp(s) {
  return s.yAxisProperty;
}